import { useCallback, useMemo, useState } from 'react';

import { getOnlyGrushkaAccess } from '@app/access/utils';
import { PF } from '@app/components/RecordModal/data/constants';
import { selectRecordModal } from '@app/components/RecordModal/model/selectors';
import { MenuTabEnum } from '@app/components/RecordModal/types';
import { ClientMenuLabel } from '@app/components/RecordModal/ui/ClientMenuLabel';
import { RecordMenuLabel } from '@app/components/RecordModal/ui/RecordMenuLabel';
import {
  useMedicalDocumentsAccessRights,
  useNewUserAccessRights,
  useUserPrint,
} from '@app/hooks';
import { useAppSelector } from '@app/store';

import type { ItemType, MenuItemType } from 'antd/es/menu/interface';

const TAB_LABELS: Record<MenuTabEnum, string> = {
  [MenuTabEnum.RecordDetails]: 'Детали записи',
  [MenuTabEnum.RecordPayment]: 'Оплата визита',
  [MenuTabEnum.RecordHistory]: 'История изменений',
  [MenuTabEnum.ClientData]: 'Данные',
  [MenuTabEnum.ClientCard]: 'Сообщения',
  [MenuTabEnum.VisitsHistory]: 'История визитов',
  [MenuTabEnum.TechnologicalMap]: 'Технологическая карта',
  [MenuTabEnum.MedicalCard]: 'Мед. карта ЕГИСЗ',
};

export const useSidebar = (tab: MenuTabEnum) => {
  const { record, clientName, unsavedRecordForm, hasChanges, mode } =
    useAppSelector(selectRecordModal);

  const {
    userAccessRights: {
      Records: { IsPayRecord },
      Clients: { IsCommunicate },
      Warehouse: { IsOn },
    },
    organizationAccessRights,
  } = useNewUserAccessRights();

  const { database } = useUserPrint();
  const onlyGrushkaAccess = getOnlyGrushkaAccess(database);
  const { hasUserRightToViewMedicalDocuments } =
    useMedicalDocumentsAccessRights();

  const warehouseModuleAllowed =
    organizationAccessRights.find(({ RightType }) => RightType === 9)
      ?.Allowed || false;

  const disabledTabs = mode === 'create' && !hasChanges;

  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  const toggleSidebar = useCallback(() => {
    setIsSidebarOpen((prev) => !prev);
  }, []);

  const closeSidebar = useCallback(() => {
    setIsSidebarOpen(false);
  }, []);

  const getMenuitems = useCallback(() => {
    const recordItem: ItemType<MenuItemType> = {
      label: <RecordMenuLabel recordId={record?.Id} />,
      key: 'record',
      children: [
        {
          label: 'Детали записи',
          key: MenuTabEnum.RecordDetails,
          ['data-testid']: 'record-details-tab',
        },
        ...(IsPayRecord
          ? [
              {
                label: 'Оплата визита',
                key: MenuTabEnum.RecordPayment,
                disabled:
                  disabledTabs ||
                  record?.IsDeleted ||
                  unsavedRecordForm?.Status === 'novisit',
                ['data-testid']: 'payment-for-visit',
              },
              {
                label: 'История изменений',
                key: MenuTabEnum.RecordHistory,
                disabled: disabledTabs || record?.IsDeleted,
                ['data-testid']: 'history-of-changes',
              },
            ]
          : []),
        ...(IsOn && warehouseModuleAllowed
          ? [
              {
                label: 'Технологическая карта',
                key: MenuTabEnum.TechnologicalMap,
                disabled: disabledTabs || record?.IsDeleted,
                ['data-testid']: 'technological-map',
              },
            ]
          : []),
        ...(onlyGrushkaAccess && hasUserRightToViewMedicalDocuments
          ? [
              {
                label: 'Мед. карта ЕГИСЗ',
                key: MenuTabEnum.MedicalCard,
                disabled: disabledTabs || record?.IsDeleted,
              },
            ]
          : []),
      ],
      type: 'group',
      className: `${PF}__sidebar-group`,
    };

    const clientItem: ItemType<MenuItemType> = {
      label: <ClientMenuLabel name={clientName} />,
      key: 'client',
      children: [
        {
          label: 'Данные',
          key: MenuTabEnum.ClientData,
          disabled: disabledTabs,
          ['data-testid']: 'data',
        },
        {
          label: 'Сообщения',
          key: MenuTabEnum.ClientCard,
          disabled: disabledTabs,
          ['data-testid']: 'client-card',
        },
        {
          label: 'История визитов',
          key: MenuTabEnum.VisitsHistory,
          disabled: disabledTabs,
          ['data-testid']: 'visit-history',
        },
      ],
      type: 'group',
      className: `${PF}__sidebar-group`,
    };

    return [recordItem, ...(IsCommunicate ? [clientItem] : [])];
  }, [
    record?.Id,
    record?.IsDeleted,
    IsPayRecord,
    disabledTabs,
    unsavedRecordForm?.Status,
    IsOn,
    warehouseModuleAllowed,
    onlyGrushkaAccess,
    hasUserRightToViewMedicalDocuments,
    clientName,
    IsCommunicate,
  ]);

  const menuItems: ItemType<MenuItemType>[] = useMemo(getMenuitems, [
    getMenuitems,
  ]);

  const currentTabLabel = TAB_LABELS[tab];

  return {
    menuItems,
    isSidebarOpen,
    toggleSidebar,
    closeSidebar,
    currentTabLabel,
  };
};
